'use client';

import { isPlainPaginationClick, paginationHref } from '@eliadmin/core/src/utils/pagination-link';

import type { PaginationLinks, PaginationMeta } from '@eliadmin/core/src/api/api-types';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import {
  isGlobalNavigation,
  startGlobalNavigation,
  useGlobalNavigation,
} from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Link from '@eliadmin/core/src/components/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';

export type PaginationProps = {
  links: Required<PaginationMeta['links']>;
  className?: {
    main?: string;
    item?: string;
    replaceItem?: string;
  };
  pageKey?: string;
  shouldReplace?: boolean;
  setPage?: (page: number) => void;
  onPageClicked?: (page: number) => void;
  scrollToElementAfterPageChange?: string;
  // When any of these values change, jump back to page 1
  resetWhen?: unknown[];
};

export function Pagination({ pageKey = 'page', ...props }: PaginationProps) {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const isNavigating = useGlobalNavigation();
  const [clicked, setClicked] = useState<number | null>(null);
  const breakpoint = useBreakpoint('sm');
  const resetSignature = JSON.stringify(props.resetWhen);
  const previousResetSignature = useRef(resetSignature);

  const getUrl = (url?: string) => {
    if (!url) return undefined;
    try {
      const page = Number(new URL(url, 'http://localhost').searchParams.get('page'));
      return paginationHref(pathname, searchParams, page, pageKey);
    } catch {
      return undefined;
    }
  };

  useEffect(() => {
    if (clicked !== null && !isNavigating) {
      setClicked(null);
      const targetElement = props.scrollToElementAfterPageChange
        ? document.getElementById(props.scrollToElementAfterPageChange)
        : null;
      targetElement?.scrollIntoView({ behavior: 'smooth' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clicked, isNavigating]);

  const shouldShowPaginationItemMobile = (
    link: PaginationLinks,
    currentItemIndex: number,
    linksLength: number,
  ) => {
    if (!props.links) return true;
    // Find the index of the active link
    const activeIndex = props.links.findIndex(link => link.active);

    // Always show the chevrons at the ends
    if (currentItemIndex === 0 || currentItemIndex === linksLength - 1) return true;

    let minIndexToShow, maxIndexToShow;

    if (activeIndex <= 2) {
      // IF PAGE FIRST OR SECOND
      minIndexToShow = 1;
      maxIndexToShow = 5;
    } else if (activeIndex >= linksLength - 4) {
      // IF PAGE LAST OR SECOND LAST
      minIndexToShow = linksLength - 6;
      maxIndexToShow = linksLength - 2;
    } else {
      minIndexToShow = activeIndex - 2;
      maxIndexToShow = activeIndex + 2;
    }

    // Determine if the current link index falls within the range to be shown
    return currentItemIndex >= minIndexToShow && currentItemIndex <= maxIndexToShow;
  };

  // Mounting page 2 (including an RSC remount) is not a filter change.
  // Signature comparison is also safe when Strict Mode replays the effect.
  useEffect(() => {
    const previous = previousResetSignature.current;
    previousResetSignature.current = resetSignature;
    if (!props.resetWhen || previous === resetSignature) return;
    // Prefer controlled pagination
    if (props.shouldReplace && props.setPage) {
      props.setPage(1);
      props.onPageClicked?.(1);
      setClicked(null);
      return;
    }

    // Preserve a filter URL written by another effect in the same commit.
    const currentSearchParams = new URLSearchParams(window.location.search);
    currentSearchParams.delete(pageKey);
    const query = currentSearchParams.toString();
    const nextUrl = `${pathname}${query ? `?${query}` : ''}${window.location.hash}`;
    if (nextUrl !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
      window.history.replaceState(null, '', nextUrl);
    }
    props.onPageClicked?.(1);
    setClicked(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetSignature]);

  return (
    <div
      className={cn('flex flex-row items-center space-x-2', 'ea-pagination', props.className?.main)}
    >
      {props.links?.map((link, i) => {
        const href = getUrl(link.url ?? undefined);
        const disabled = !href;
        const pending = clicked !== null || isNavigating;
        const targetPage = href
          ? Number(new URL(href, 'http://localhost').searchParams.get(pageKey) ?? '1')
          : undefined;

        if (
          props.links?.length &&
          !breakpoint &&
          !shouldShowPaginationItemMobile(link, i, props.links.length)
        )
          return null;

        const content =
          i === 0 ? (
            <LuChevronLeft className="h-4 w-4" />
          ) : i === props.links!.length - 1 ? (
            <LuChevronRight className="h-4 w-4" />
          ) : (
            link.label
          );
        const accessibleLabel = i === 0
          ? 'Predchádzajúca stránka'
          : i === props.links!.length - 1
            ? 'Ďalšia stránka'
            : undefined;

        return (
          <Button
            aria-label={accessibleLabel}
            size={'none'}
            key={`${link.label}-${link.url}-${i}`}
            className={cn(
              'col-span-1 max-h-12 min-h-12 min-w-12 max-w-12 !p-0',
              'ea-pagination__item',
              props.className?.item,
            )}
            asChild={!disabled && !pending}
            type={disabled || pending ? 'button' : undefined}
            disabled={disabled || pending}
            variant={link.active ? 'dark' : disabled ? 'ghost' : 'outline'}
            onClick={event => {
              if (disabled || pending) {
                event.preventDefault();
                return;
              }
              if (!isPlainPaginationClick(event)) return;
              if (props.shouldReplace && props.setPage) event.preventDefault();
              if (!targetPage) return;
              if ((searchParams.get(pageKey) ?? '1') === String(targetPage)) {
                event.preventDefault();
                props.onPageClicked?.(targetPage);
                return;
              }
              if (!isGlobalNavigation() && !props.shouldReplace) {
                startGlobalNavigation();
                setClicked(i);
                props.onPageClicked?.(targetPage);
                return;
              }

              if (props.shouldReplace && props.setPage) {
                props.setPage(targetPage);
                setClicked(i);
                props.onPageClicked?.(targetPage);
              }
            }}
            isLoading={clicked === i}
          >
            {disabled || pending ? (
              content
            ) : !props.shouldReplace || !props.setPage ? (
              <Link aria-label={accessibleLabel} prefetchOnHover={!link.active} prefetch={false} href={href ?? '#'}>
                {content}
              </Link>
            ) : (
              <Link
                aria-label={accessibleLabel}
                href={href!}
                prefetch={false}
                prefetchOnHover={false}
                className={cn(
                  'ea-pagination__item-replace-custom cursor-pointer',
                  disabled && 'cursor-default',
                  link.active && 'bg-primary text-background',
                  props.className?.replaceItem,
                )}
              >
                {content}
              </Link>
            )}
          </Button>
        );
      })}
    </div>
  );
}
